import { Link } from "react-router-dom";
import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Target, Eye, Shield, Users, Building2, Compass, MapPin } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";

const VALUES = [
  { icon: Target, title: "Precision", body: "Every deal is underwritten to institutional IRR bands. No gut calls, no retail heuristics — just data-driven conviction." },
  { icon: Shield, title: "Integrity", body: "Full title indemnity on every transaction. We publish our fee structure upfront and never take both sides of a deal." },
  { icon: Eye, title: "Transparency", body: "Weekly dashboards, real-time rent ledgers and quarterly asset reviews. You see exactly what we see." },
  { icon: Users, title: "Partnership", body: "We succeed only when you do. Aligned incentives, milestone-linked payments and shared-risk JV structures." },
];

const TEAM = [
  { name: "Founding Partner", role: "Acquisitions & Strategy", desc: "15+ years in institutional real estate. Former CBRE India, Knight Frank." },
  { name: "Managing Partner", role: "Construction & Delivery", desc: "Civil engineering veteran. 2M+ sqft delivered across Bengaluru." },
  { name: "NRI Desk Lead", role: "Remote Governance", desc: "Chartered accountant with deep NRI taxation and repatriation expertise." },
  { name: "Legal & Compliance", role: "Title & RERA", desc: "Ex-district court advocate. 500+ title audits. RERA registration specialist." },
];

const MILESTONES = [
  { year: "2026", label: "Founded", desc: "Build Brigade Enterprises established in Hoskote, Bengaluru" },
  { year: "2026", label: "First JV", desc: "First Joint Venture development mandate signed in Devanahalli corridor" },
  { year: "2026", label: "NRI Desk", desc: "Dedicated NRI Property Management desk launched for UAE & US clients" },
  { year: "2026", label: "Digital-first", desc: "Full-stack digital platform live with AI-powered property assistant" },
];

const STATS = [
  { value: 500, suffix: "+", label: "Title Audits Completed" },
  { value: 2, suffix: "M+", label: "Sqft Delivered" },
  { value: 150, suffix: "Cr+", label: "Portfolio Value (₹)" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
];

function AnimatedCounter({ value, suffix = "", inView }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const stepTime = 16;
    const steps = duration / stepTime;
    const increment = value / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, [inView, value]);

  return <span>{count.toLocaleString()}{suffix}</span>;
}

function StatsGrid() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div ref={ref}>
      <div className="chapter-num mb-4">By The Numbers</div>
      <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">Our track record.</h2>
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="border-t border-[#D4AF37]/40 pt-8"
            data-testid={`stat-${i}`}
          >
            <div className="font-serif text-[48px] md:text-[64px] leading-none tracking-tight text-[#D4AF37]">
              <AnimatedCounter value={s.value} suffix={s.suffix} inView={inView} />
            </div>
            <div className="mt-4 text-[12px] tracking-[2px] uppercase text-[#EDE6D3]/60">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div className="pt-[112px]">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> About · Build Brigade Enterprises</div>
        <h1 className="display-1 max-w-6xl" data-testid="about-title">
          <MaskedLines lines={["Built for Bengaluru.", "Built to last."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Build Brigade Enterprises is a new-generation real estate desk purpose-built for strategic infrastructure and construction. From land aggregation to institutional exit — one desk, one mandate, one city.
        </p>
      </section>

      {/* Brand Identity */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="brand-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <div className="flex justify-center">
              <img src="/bbe-logo.png" alt="BBE Logo" className="w-64 h-64 object-contain" data-testid="about-logo" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-7" delay={0.1}>
            <div className="chapter-num mb-4">The Compass & Skyline</div>
            <h2 className="font-serif text-[38px] md:text-[52px] leading-[1.05] tracking-tight">Strategic Infrastructure & Construction</h2>
            <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70 max-w-lg">
              Our compass symbolizes direction and strategic foresight — guiding clients through every phase of the real estate lifecycle. The skyline represents what we build: enduring structures that define the Bengaluru horizon.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center gap-2 text-[12px] tracking-[2px] uppercase text-[#D4AF37]">
                <Compass size={16} /> Direction
              </div>
              <span className="text-[#EDE6D3]/20">|</span>
              <div className="flex items-center gap-2 text-[12px] tracking-[2px] uppercase text-[#D4AF37]">
                <Building2 size={16} /> Infrastructure
              </div>
              <span className="text-[#EDE6D3]/20">|</span>
              <div className="flex items-center gap-2 text-[12px] tracking-[2px] uppercase text-[#D4AF37]">
                <MapPin size={16} /> Bengaluru
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="vision-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div className="card-sand p-10 md:p-14 h-full">
              <div className="eyebrow mb-6">Our Vision</div>
              <h3 className="font-serif text-[32px] md:text-[40px] leading-[1.1] tracking-tight">
                To be Bengaluru's most trusted name in strategic real estate.
              </h3>
              <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">
                We envision a real estate ecosystem where every transaction is transparent, every construction meets institutional spec, and every investor — domestic or NRI — has a dedicated desk that speaks their language.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-sand p-10 md:p-14 h-full">
              <div className="eyebrow mb-6">Our Mission</div>
              <h3 className="font-serif text-[32px] md:text-[40px] leading-[1.1] tracking-tight">
                Engineer value across the entire lifecycle.
              </h3>
              <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">
                From sourcing off-market land banks and executing turnkey construction to managing assets for NRIs and structuring institutional exits — we compress the entire real estate value chain into a single mandate.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="values-section">
        <div className="chapter-num mb-4">Core Values</div>
        <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">What we stand for.</h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <div className="card-sand p-8 h-full">
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center mb-6">
                  <v.icon size={22} color="#D4AF37" />
                </div>
                <h4 className="font-serif text-[24px] leading-tight">{v.title}</h4>
                <p className="mt-4 text-[13px] leading-[1.8] text-[#EDE6D3]/65">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Leadership Team */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="team-section">
        <div className="chapter-num mb-4">Leadership</div>
        <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">The desk behind BBE.</h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TEAM.map((t, i) => (
            <Reveal key={t.role} delay={i * 0.06}>
              <div className="card-sand p-8 h-full">
                <div className="w-16 h-16 bg-[#0E2A4E] border border-[#D4AF37]/30 flex items-center justify-center mb-6">
                  <span className="font-display text-[20px] text-[#D4AF37]">{t.name.split(" ").map(w => w[0]).join("")}</span>
                </div>
                <div className="eyebrow text-[#D4AF37] mb-2">{t.role}</div>
                <h4 className="font-serif text-[22px] leading-tight">{t.name}</h4>
                <p className="mt-3 text-[13px] leading-[1.7] text-[#EDE6D3]/60">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Animated Stats */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="stats-section">
        <StatsGrid />
      </section>

      {/* Timeline */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="timeline-section">
        <div className="chapter-num mb-4">Journey</div>
        <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">Milestones.</h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          {MILESTONES.map((m, i) => (
            <Reveal key={m.label} delay={i * 0.08}>
              <div className="border-t border-[#D4AF37]/40 pt-8">
                <div className="chapter-num">{m.year}</div>
                <h4 className="mt-3 font-serif text-[24px] leading-tight">{m.label}</h4>
                <p className="mt-3 text-[13px] leading-[1.7] text-[#EDE6D3]/60">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32 pb-8">
        <Reveal>
          <div className="bg-[#EDE6D3] text-[#0A2342] p-10 md:p-20 relative grain overflow-hidden">
            <div className="chapter-num text-[#D4AF37]">Get In Touch</div>
            <h2 className="mt-6 font-serif text-[44px] md:text-[80px] leading-[0.95] tracking-tight max-w-4xl">
              Let's build something together.
            </h2>
            <p className="mt-8 max-w-xl text-[15px] leading-[1.8] text-[#0A2342]/70">
              Whether you're an investor, landowner, or NRI looking for trusted property governance — we'd love to hear from you.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-bronze" data-testid="about-cta-contact">Start a Conversation <ArrowUpRight size={14} /></Link>
              <Link to="/properties" className="text-[12px] tracking-[2px] uppercase text-[#0A2342] link-underline">Browse Properties</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
